import { useState, useEffect, useRef, useCallback } from "react";
import { X, Check, Crown, Sparkles } from "lucide-react";
import { supabase } from "../supabase";
import type { Session } from "@supabase/supabase-js";

interface SubscriptionModalProps {
  open: boolean;
  session: Session | null;
  onClose: () => void;
  onSuccess: () => void;
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  lang: "ar" | "en";
  complimentary?: boolean;
}

export function SubscriptionModal({ open, session, onClose, t, dir, lang, complimentary = false }: SubscriptionModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open || !session) return;
    (async () => {
      const { data } = await supabase
        .from("subscriptions")
        .select("plan,status")
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (data) {
        setCurrentPlan(data.plan);
        setStatus(data.status);
      }
    })();
  }, [open, session]);

  // Focus trap + Escape + focus restoration
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement;
    closeBtnRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
      if (e.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  const handleClose = useCallback(() => { setError(null); onClose(); }, [onClose]);

  if (!open) return null;

  const isRtl = dir === "rtl";
  const isActive = status === "active" || status === "trialing";

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" dir={dir}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sub-modal-title"
        className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#0A2240] to-[#0D1B2E] px-6 py-8 flex flex-col items-center gap-3">
          <button
            ref={closeBtnRef}
            onClick={handleClose}
            aria-label={t("closeDialog")}
            className="absolute top-4 end-4 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h2 id="sub-modal-title" className="text-xl font-bold text-white">{complimentary ? (lang === "ar" ? "وصول خاص دائم" : "Lifetime complimentary access") : t("subTitle")}</h2>
          <p className="text-sm text-white/70 text-center leading-relaxed">{complimentary ? (lang === "ar" ? "حسابك مفعّل بدون دفع أو تاريخ انتهاء" : "Your account is active with no payment or expiry date") : t("subSubtitle")}</p>
        </div>

        {/* Current plan badge */}
        {isActive && !complimentary && (
          <div className="px-6 pt-4">
            <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-xl px-4 py-2.5">
              <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
                {status === "trialing" ? t("subTrialActive") : t("subActive")}
                {currentPlan && ` — ${currentPlan === "monthly" ? t("subMonthly") : t("subYearly")}`}
              </span>
            </div>
          </div>
        )}

        {/* Plans */}
        {complimentary ? (
          <div className="px-6 py-6 space-y-4 text-sm text-stone-700 dark:text-slate-200">
            <p>{lang === "ar" ? "هذا الحساب من الحسابات الخاصة التي منحها صاحب التطبيق وصولًا مجانيًا دائمًا. لا يحتاج اشتراكًا مدفوعًا أو تجديدًا." : "The app owner granted this account permanent complimentary access. No paid subscription or renewal is required."}</p>
            <p>{lang === "ar" ? "رصيد الذكاء الاصطناعي: 100 رصيد يتجدد شهريًا، بحد أقصى 10 أرصدة يوميًا." : "AI allowance: 100 credits renewed monthly, with a maximum of 10 credits per day."}</p>
          </div>
        ) : <div className="px-6 py-5 space-y-3">
          {/* Payment unavailable notice */}
          <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 px-4 py-3">
            <p className="text-xs text-amber-700 dark:text-amber-400 font-medium text-center leading-relaxed">
              {t("subCheckoutNotWired")}
            </p>
          </div>

          {/* Monthly */}
          <div className={`relative rounded-2xl border-2 p-4 transition-all ${!isActive ? "border-blue-500 bg-blue-50/50 dark:bg-blue-500/10" : "border-stone-200 dark:border-slate-700"}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="font-bold text-stone-800 dark:text-slate-100">{t("subMonthly")}</span>
              </div>
              <span className="text-lg font-black text-stone-800 dark:text-slate-100">
                {lang === "ar" ? "$4.99" : "$4.99"}
                <span className="text-xs font-normal text-stone-400">/{t("subMonth")}</span>
              </span>
            </div>
            <ul className="space-y-1.5 mb-3">
              {[t("subFeatureCredits100"), t("subFeatureCreditsDaily10"), t("subFeatureAll")].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-stone-600 dark:text-slate-300">
                  <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => setError(t("subCheckoutNotWired"))}
              disabled
              aria-disabled="true"
              className="w-full py-3 rounded-xl bg-stone-200 dark:bg-slate-700 text-stone-500 dark:text-slate-400 font-bold text-sm cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isActive ? <Check className="w-4 h-4" /> : null}
              {isActive ? t("subCurrentPlan") : t("subChooseMonthly")}
            </button>
          </div>

          {/* Yearly — best value */}
          <div className={`relative rounded-2xl border-2 p-4 transition-all ${!isActive ? "border-amber-500 bg-amber-50/50 dark:bg-amber-500/10" : "border-stone-200 dark:border-slate-700"}`}>
            <div className="absolute -top-2.5 start-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-black px-3 py-0.5 rounded-full shadow-md whitespace-nowrap" style={isRtl ? { right: "50%", transform: "translateX(50%)" } : undefined}>
              {t("subBestValue")}
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <span className="font-bold text-stone-800 dark:text-slate-100">{t("subYearly")}</span>
              </div>
              <div className="text-end">
                <span className="text-lg font-black text-stone-800 dark:text-slate-100">
                  {lang === "ar" ? "$39.99" : "$39.99"}
                </span>
                <span className="text-xs font-normal text-stone-400 block leading-none">{t("subPerYear")}</span>
              </div>
            </div>
            <ul className="space-y-1.5 mb-3">
              {[t("subFeatureCredits100"), t("subFeatureCreditsDaily10"), t("subFeatureAll")].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-stone-600 dark:text-slate-300">
                  <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => setError(t("subCheckoutNotWired"))}
              disabled
              aria-disabled="true"
              className="w-full py-3 rounded-xl bg-stone-200 dark:bg-slate-700 text-stone-500 dark:text-slate-400 font-bold text-sm cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isActive ? <Check className="w-4 h-4" /> : null}
              {isActive ? t("subCurrentPlan") : t("subChooseYearly")}
            </button>
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 text-center font-medium" role="alert">{error}</p>
          )}

          <p className="text-[11px] text-stone-400 dark:text-slate-500 text-center pt-1">
            {t("subTrialHint")}
          </p>
        </div>}
      </div>
    </div>
  );
}
